import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch, Action } from 'redux';
import { AppStates } from '../../reducers/app';
import ToolIconButton from '../common/ToolIconButton';
import ToggleToolIconButton from '../common/ToggleToolIconButton';
import ToolbarItemScroller from '../common/toolbar-item-scroller/ToolbarItemScroller';
import LineWidthRadioButtonSet from './LineWidthRadioButtonSet';
import LineColorRadioButtonSet from '../common/LineColorRadioButtonSet';
import MaskAndBalloonSettingsDialog from '../textbox/MaskAndBalloonSettingsDialog';
import * as PageAction from '../../actions/PageAction';
import * as TTSAction from '../../tts/actions/TTSAction';
import { PagesState, TabType, tabFocusSelf } from '../../reducers/pages';
import { PreferenceState } from '../../reducers/preference';
import {
  getActiveFrameId, getReflowablePageForTab, getBookmarkReferenceIfPageableTab,
} from '../../helpers/pages';
import { Publication, ExtraToolDefinition, ExtraToolAction } from '../../types/epub';
import { getDefaultRendition, supportsReflowableRendition } from '../../utils/epub';
import { UIModeState } from '../../reducers/uimode';
import { TTSToolbar } from '../tts/TTSToolbar';
import { SpeechState } from '../../tts/reducers/speech';
import {
  setTTSMode, setRegionZoomMode, setUIMode, ToolbarDirection,
} from '../../actions/UIModeAction';

import {
  defaultIconSize, toolbarSize, toolbarBackgroundLightColor,
} from '../../style/values';

import { getGlobalFrameStore } from '../../services/FrameStore';
import { mapStateToZoomable } from '../../selectors/zoomingSelector';
import { ViewerToContent } from '../../types/messages';
import SecondaryToolbar from '../common/SecondaryToolbar';
import { DrawingMode, LineType } from '../../drawing/types';
import { DrawingToolState, DrawingAttr } from '../../drawing/reducers/drawingTool';
import DrawingToolAction from '../../drawing/actions/DrawingToolAction';
import { clearEverything } from '../../drawing/actions/DrawingAction';
import {
  setGeneralRuby, ColorScheme, setColorScheme, setFontFamily, FontFamily, setFontSize,
  setLineHeight,
} from '../../actions/PreferenceAction';
import SettingPopup from '../common/SettingPopup';
import { getEditorKeyAndPage } from '../../helpers/integratedDrawings';
import {
  canUndoDrawing, IntegratedDrawingState, canRedoDrawing, getPrevDrawingPage,
  getNextDrawingPage,
} from '../../reducers/integratedDrawing';
import HistoryAction from '../../actions/HistoryAction';
import MarkerMenu from '../marker/MarkerMenu';
import { setSelectionState } from '../../actions/SelectionAction';
import { SelectionType, SelectionState } from '../../reducers/selection';
import { UIMode } from '../../../../../common/src/ts/types/UIMode';
import ZoomControl from '../common/ZoomControl';
import LineTypeRadioButtonSet from './LineTypeRadioButtonSet';
import { Vector2D } from '../../types/common';
import { BookmarksState } from '../../bookmark/reducers/bookmark';
import PrimaryToolbar from '../common/PrimaryToolbar';

export interface HeaderToolbarProps {
  pages: PagesState;
  publication: Publication;
  preference: PreferenceState;
  drawingTool: DrawingToolState;
  integratedDrawing: IntegratedDrawingState;
  zoomInEnabled: boolean;
  zoomOutEnabled: boolean;
  speech: SpeechState;
  uimode: UIModeState;
  selection: SelectionState;
  bookmarks: BookmarksState;
  dispatch: Dispatch<Action>;

  // TODO : refactor
  onPlay: () => any;
  onPause: () => any;
  onResume: () => any;
  onReset: () => any;

  onSendToClipbook: () => void;
  onOpenFileDialog: (cb: (filePath: string) => void) => void;
}

enum SecondaryToolbarMode {
  None,
  UIMode,
  Zoom,
  SNE,
  Undo,
  ImagePasting,
  ClearEverything,
  ExtraTools,
  Color,
  LineWidth,
  LineType,
}

enum Level {
  Base,
  FreehandPen,
  LineType,
  CircleType,
  RectangleType,
  TriangleType,
  PolygonType,
  TTS,
  Marker,
}

interface HeaderToolbarState {
  level: Level;
  // 書き込みツール2階層目の消しゴム/削除を持つ前どのモードだったかを保持する
  // FIXME: 根本的にやり方が良くない
  lastDrawingMode: DrawingMode;
  secondaryToolbar: SecondaryToolbarMode;
  showSNEColorSetting: boolean; // TODO : 詰め込みすぎなのでComponent分離したい
  showReflowSetting: boolean;
  showMaskAndBalloonSettingsDialog: boolean;
  showMultiroleLinkDialog: boolean;
}

const sectionStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
};

const sectionStartStyle: React.CSSProperties = {
  ...sectionStyle,
  flex: `0 0 ${defaultIconSize * 2}px`,
  order: -1,
  backgroundColor: toolbarBackgroundLightColor,
};

const selectableColors = ['red', 'blue', 'yellow', 'green', 'black'];
const selectableWidths = [3, 10, 20, 30, 50];
// 消しゴムが大きい方が使いやすいという意見があるので、試験的に5倍にしている
// 当初は10倍にしていたが削除範囲と実際の削除のずれが目立ったので、目立ちづらいところとして5倍に抑えた
const selectableHanderasingWidths = selectableWidths.map((w: number) => w * 5);

class HeaderToolbar extends React.PureComponent<HeaderToolbarProps, HeaderToolbarState> {
  private imageFileInput: HTMLInputElement | null;
  private baseToolbarScrollAmount: Vector2D = { x: 0, y: 0 };

  public constructor(props: HeaderToolbarProps) {
    super(props);

    this.imageFileInput = null;

    this.state = {
      level: Level.Base,
      lastDrawingMode: DrawingMode.Handwriting,
      secondaryToolbar: SecondaryToolbarMode.None,
      showSNEColorSetting: false,
      showReflowSetting: false,
      showMaskAndBalloonSettingsDialog: false,
      showMultiroleLinkDialog: false,
    };
  }

  private handleOpenReflowable = () => {
    if (supportsReflowableRendition(this.props.publication)) {
      const { tabs, activeTab } = this.props.pages;
      const title = `テキスト表示 | ${tabs[activeTab].title}`;
      const pageIndex = getReflowablePageForTab(tabs[activeTab], this.props.publication);
      if (pageIndex !== null) {
        this.props.dispatch(PageAction.openReflowable(title, pageIndex));
      }
    } else {
      getGlobalFrameStore().postMessage(getActiveFrameId(this.props.pages),
                                        ViewerToContent.MessageType.GetReflowableContent);
    }
  }

  private createUIModeSubmenus() {
    const { dispatch, uimode, drawingTool } = this.props;
    const switchToDrawingMode = () => {
      setUIMode(UIMode.Drawing, dispatch);
      dispatch(DrawingToolAction.setMode(DrawingMode.Selection));
    };
    const handleButtonClick = (mode: UIMode) => {
      setUIMode(mode, dispatch);
      this.setState({ secondaryToolbar: SecondaryToolbarMode.None });
    };

    const isObjectSeletionMode =
      (uimode.mode === UIMode.Drawing) && (drawingTool.mode === DrawingMode.Selection);

    return <>
      <ToolIconButton
        onClick={() => handleButtonClick(UIMode.Touch)}
        active={this.props.uimode.mode === UIMode.Touch}
        iconName="pointer"
        title="紙面操作 + ズーム/パン"
        ariaLabel="Touch" />
      <ToolIconButton
        onClick={() => handleButtonClick(UIMode.ZoomAndPan)}
        active={this.props.uimode.mode === UIMode.ZoomAndPan}
        text="ズーム/パン"
        title="ズーム・パン"
        ariaLabel="Zoom/Pan" />
      <ToolIconButton
        onClick={() => handleButtonClick(UIMode.Mouse)}
        active={this.props.uimode.mode === UIMode.Mouse}
        text="紙面操作"
        title="紙面操作"
        ariaLabel="Mouse" />
      <ToolIconButton
        onClick={switchToDrawingMode}
        active={isObjectSeletionMode}
        text="オブジェクト選択"
        title="オブジェクト選択"
        ariaLabel="Drawing" />
    </>;
  }

  private createZoomSubmenus() {
    return <ZoomControl />;
  }

  private setTTSMode(ttsMode: boolean) {
    this.setState({
      level: ttsMode ? Level.TTS : Level.Base,
      secondaryToolbar: SecondaryToolbarMode.None,
    });
    setTTSMode(ttsMode, this.props.dispatch);
  }

  private createSNESubmenus() {
    const { dispatch } = this.props;
    const { activeTab } = this.props.pages;
    let reflowEnabled = supportsReflowableRendition(this.props.publication);
    if (!reflowEnabled) {
      const tab = this.props.pages.tabs[activeTab];
      if (tab.focus === tabFocusSelf) {
        if (tab.type === TabType.LinearPages) {
          const item = getDefaultRendition(this.props.publication).linearItems[tab.currentPage];
          if (item.fxl) {
            if (item.supportsReflowableContentGeneration) {
              reflowEnabled = true;
            }
          }
        }
      }
    }

    // TODO : 別Componentに分離
    let colorSetting: JSX.Element | null = null;
    if (this.state.showSNEColorSetting) {
      const itemStyle = {
        display: 'inline-block',
        width: '240px',
        height: '85px',
        margin: '12px 24px',
        border: '1px solid gray',
        lineHeight: '85px',
        textIndent: '1em',
        fontSize: '24px',
        color: 'gray',
      };

      const schemes =
        [ColorScheme.Scheme1, ColorScheme.Scheme2, ColorScheme.Scheme3];
      const items = schemes.map(scheme => (
        <div style={itemStyle} key={scheme} onClick={() => dispatch(setColorScheme(scheme))}>
          パターン{scheme + 1}
        </div>
      ));
      colorSetting = (
        <SettingPopup
          title="白黒反転設定"
          onClose={() => this.setState({ showSNEColorSetting: false })}>
          {items}
        </SettingPopup>
      );
    }

    let reflowSetting: JSX.Element | null = null;
    if (this.state.showReflowSetting) {
      const createFontFamilyButton = (family: FontFamily, name: string, active: boolean) => {
        const style: React.CSSProperties = {
          display: 'inline-block',
          width: '160px',
          height: '85px',
          lineHeight: '85px',
          textAlign: 'center',
          boxSizing: 'border-box',
          fontSize: '16px',
          margin: '16px',
          background: '#aaa',
        };
        if (active) {
          style.border = '2px solid white';
        }
        return <a onClick={() => dispatch(setFontFamily(family))} style={style}>{name}</a>;
      };

      const { fontSize, lineHeight } = this.props.preference;
      const sizeButtonStyle = {
        display: 'inline-block',
        width: '40px',
        height: '40px',
        background: '#aaa',
        border: '1px solid white',
        margin: '0 10px',
        fontSize: '16px',
      };
      reflowSetting = (
        <SettingPopup
          title="リフロー設定"
          onClose={() => this.setState({ showReflowSetting: false })}>
          <div>
            {createFontFamilyButton(FontFamily.Textbook, '教科書体',
                                    this.props.preference.fontFamily === FontFamily.Textbook)}
            {createFontFamilyButton(FontFamily.Serif, '明朝体',
                                    this.props.preference.fontFamily === FontFamily.Serif)}
            {createFontFamilyButton(FontFamily.SansSerif, 'ゴシック体',
                                    this.props.preference.fontFamily === FontFamily.SansSerif)}
          </div>
          <div style={{ padding: '12px', lineHeight: '40px', textAlign: 'center' }}>
            <h3 style={{ textAlign: 'left', margin: '0 0 0 1em' }}>フォントサイズ</h3>
            <a style={sizeButtonStyle}
              onClick={() => { if (fontSize > 50) { dispatch(setFontSize(fontSize - 10)); } }}>
              -
            </a>
            {fontSize}%
            <a style={sizeButtonStyle}
              onClick={() => { if (fontSize < 200) { dispatch(setFontSize(fontSize + 10)); } }}>
              +
            </a>
          </div>
          <div style={{ padding: '12px', lineHeight: '40px', textAlign: 'center' }}>
            <h3 style={{ textAlign: 'left', margin: '0 0 0 1em' }}>行間</h3>
            <input type="range" min="1" max="4" step="0.1" value={lineHeight}
              onChange={el => dispatch(setLineHeight(+el.currentTarget.value))} />
          </div>
        </SettingPopup>
      );
    }

    return <>
      <ToolIconButton
        onClick={() => { if (reflowEnabled) { this.handleOpenReflowable(); } }}
        disabled={!reflowEnabled}
        iconName="sne-reflow"
        title="リフロー"
        ariaLabel="Reflow" />
      <ToolIconButton
        onClick={() => dispatch(setGeneralRuby(!this.props.preference.enableGeneralRuby))}
        active={this.props.preference.enableGeneralRuby}
        iconName="sne-general-ruby"
        title="総ルビ"
        aria-label="general ruby" />
      <ToolIconButton
        onClick={() => this.setState({ showSNEColorSetting: true })}
        active={this.state.showSNEColorSetting}
        iconName="sne-color-setting"
        title="白黒反転設定"
        aria-label="Color setting" />
      <ToolIconButton
        onClick={() => this.setTTSMode(!this.props.uimode.ttsMode)}
        iconName="sne-tts"
        active={this.props.uimode.ttsMode}
        title="TTS"
        ariaLabel="TTS" />
      <ToolIconButton
        onClick={() => this.setState({ showReflowSetting: true })}
        active={this.state.showReflowSetting}
        iconName="sne-reflow-setting"
        title="リフロー設定"
        aria-label="Reflow setting" />
      {colorSetting}
      {reflowSetting}
    </>;
  }

  private createUndoSubmenus() {
    const { integratedDrawing, pages, publication } = this.props;
    const canRedo = canRedoDrawing(integratedDrawing, getEditorKeyAndPage(pages, publication));

    return <>
      <ToolIconButton
        onClick={this.handleRedoDrawing}
        disabled={!canRedo}
        iconName="redo"
        title="REDO"
        aria-label="REDO" />
    </>;
  }

  private handleChangeImageFile = () => {
    let selectedFile;

    if (this.imageFileInput === null) {
      selectedFile = null;
    } else {
      const { files } = this.imageFileInput;
      if (files === null || files.length === 0) {
        selectedFile = null;
      } else {
        selectedFile = files[0];
      }
    }

    this.props.dispatch(DrawingToolAction.setImageToPaste(selectedFile));
  }

  private createImagePastingSubmenus() {
    return <>
      <ToggleToolIconButton
        onClick={(isOn) => {
          this.props.dispatch(DrawingToolAction.setIsTransparent(isOn));
        }}
        isOn={this.currentDrawingAttr().isTransparent}
        active={this.currentDrawingAttr().isTransparent}
        iconNameOff="transparent"
        iconNameOn="transparent"
        title="半透過"
        ariaLabel="toggle-transparent" />

      <ToolIconButton
        onClick={() => {
          setUIMode(UIMode.Drawing, this.props.dispatch);
          this.props.dispatch(DrawingToolAction.setMode(DrawingMode.ImagePasting));
        }}
        active={this.props.drawingTool.mode === DrawingMode.ImagePasting}
        iconName="image-pasting"
        title="画像貼り付け"
        aria-label="image-pasting" />

      <label htmlFor="image-input" style={{
        fontSize: '11px',
        textAlign: 'center',
        width: '48px',
        lineHeight: '48px',
        background: 'rgba(0, 0, 0, 0.1)',
      }}>
        選択
        <input
          id="image-input"
          ref={el => this.imageFileInput = el}
          style={{ display: 'none' }}
          onChange={this.handleChangeImageFile}
          type="file" />
      </label>
    </>;
  }

  private createClearEverythingSubmenus() {
    return <>
      <ToolIconButton
        onClick={() => {
          if (window.confirm('このページの書き込みをすべて削除します。本当によろしいですか？')) {
            this.props.dispatch(clearEverything());
          }
        }}
        text="全消去"
        title="オブジェクト全消去"
        aria-label="Clear Everything" />
    </>;
  }

  private createActionForExtraTool(tool: ExtraToolDefinition): () => void {
    const { dispatch } = this.props;
    switch (tool.action) {
      case ExtraToolAction.GoToPage:
        // do nothing.
        return () => {};
      case ExtraToolAction.OpenURL:
        return () => {
          window.open(tool.url);
        };
      case ExtraToolAction.OpenTab:
        return () => {
          dispatch(PageAction.openNewTab(tool.path, 'タイトルは未実装', 'faviconは未実装', true));
        };
      case ExtraToolAction.OpenPopup:
        return () => {
          // FIXME : ポップアップ表示中に再度クリックするとエラーになる
          // dispatch(PageAction.openPopup(tool.path, 'タイトルは未実装'));
          alert('not implemented');
        };
      case ExtraToolAction.SendMessage:
        return () => {
          getGlobalFrameStore().broadcastContentMessage(tool.data);
        };
    }
  }

  private createExtraToolsSubmenus() {
    const { publication } = this.props;
    const definitions = publication.extraToolDefinitions; // TODO : 現在itemの設定でフィルタ
    return definitions.map((definition) => {
      return (
        <ToolIconButton
          key={definition.id}
          onClick={this.createActionForExtraTool(definition)}
          text={definition.name}
          title={definition.name}
          ariaLabel={definition.id} />
      );
    });
  }

  private createColorSubmenus() {
    return <LineColorRadioButtonSet
      activeIndex={this.lineColorActiveIndex()}
      disabled={this.props.drawingTool.mode === DrawingMode.ObjectRemoval}
      onClick={this.handleSelectLineColor} />;
  }

  private createLineWidthSubmenus() {
    return <LineWidthRadioButtonSet
      activeIndex={this.lineWidthActiveIndex()}
      disabled={this.props.drawingTool.mode === DrawingMode.ObjectRemoval}
      onClick={this.handleSelectLineWidth} />;
  }

  private createLineTypeSubmenus() {
    return <LineTypeRadioButtonSet
      activeIndex={this.lineTypeActiveIndex()}
      disabled={this.props.drawingTool.mode === DrawingMode.ObjectRemoval}
      onClick={(type: LineType) => this.props.dispatch(DrawingToolAction.setLineType(type))} />;
  }

  private createToggleSecondaryButtons = (
    disableMode: DrawingMode = DrawingMode.ObjectRemoval,
  ): JSX.Element => {
    return <>
      <ToolIconButton
        onClick={() => this.toggleSecondaryToolbarOpen(SecondaryToolbarMode.LineType)}
        active={this.state.secondaryToolbar === SecondaryToolbarMode.LineType}
        disabled={this.props.drawingTool.mode === disableMode}
        iconName={this.lineTypeIconName()}
        title="線の種類設定"
        ariaLabel="select-line-type" />
      <ToolIconButton
        onClick={() => this.toggleSecondaryToolbarOpen(SecondaryToolbarMode.LineWidth)}
        active={this.state.secondaryToolbar === SecondaryToolbarMode.LineWidth}
        disabled={this.props.drawingTool.mode === disableMode}
        iconName={`line-width-${this.lineWidthActiveIndex() + 1}`}
        title="線の太さ設定"
        ariaLabel="select-line-width" />
      <ToolIconButton
        onClick={() => this.toggleSecondaryToolbarOpen(SecondaryToolbarMode.Color)}
        active={this.state.secondaryToolbar === SecondaryToolbarMode.Color}
        disabled={this.props.drawingTool.mode === disableMode}
        iconName={`colorchip-${this.currentDrawingAttr().lineColor}`}
        title="色設定"
        ariaLabel="select-color" />
    </>;
  }

  private createObjectDrawingToolButtons = (): JSX.Element => {
    return <>
        <ToggleToolIconButton
          onClick={(isOn) => {
            this.props.dispatch(DrawingToolAction.setIsTransparent(isOn));
          }}
          isOn={this.currentDrawingAttr().isTransparent}
          disabled={this.props.drawingTool.mode === DrawingMode.ObjectRemoval}
          iconNameOff="solid-line"
          iconNameOn="transparent-line"
          title="実線 半透過"
          ariaLabel="toggle-line-transparent" />
        <ToggleToolIconButton
          onClick={(isOn) => {
            this.props.dispatch(DrawingToolAction.setIsFilled(isOn));
          }}
          isOn={this.currentDrawingAttr().isFilled}
          active={this.currentDrawingAttr().isFilled}
          disabled={this.props.drawingTool.mode === DrawingMode.ObjectRemoval}
          iconNameOff="paint-style"
          iconNameOn="paint-style"
          title="塗り"
          ariaLabel="paint-style" />
      {this.createToggleSecondaryButtons()}
      {this.createObjectRemovalButton()}
    </>;
  }

  private createObjectRemovalButton = (): JSX.Element => {
    return (
      <ToggleToolIconButton
        onClick={(isOn) => {
          if (isOn) {
            this.props.dispatch(DrawingToolAction.setMode(DrawingMode.ObjectRemoval));
            this.setState({ secondaryToolbar: SecondaryToolbarMode.ClearEverything });
          } else {
            this.props.dispatch(DrawingToolAction.setMode(this.state.lastDrawingMode));
            this.setState({ secondaryToolbar: SecondaryToolbarMode.None });
          }
        }}
        isOn={this.props.drawingTool.mode === DrawingMode.ObjectRemoval}
        active={this.props.drawingTool.mode === DrawingMode.ObjectRemoval}
        iconNameOn="object-removal"
        iconNameOff="object-removal"
        title="削除"
        aria-label="object-removal" />
    );
  }

  private createStraightLineLevel = (
    isVertical: boolean,
    flexDirectionStyle: React.CSSProperties,
  ): JSX.Element => {
    return <>
      <section style={{ ...sectionStartStyle, ...flexDirectionStyle }}>
        <ToolIconButton
          onClick={() => {
            this.setState({ level: Level.Base, secondaryToolbar: SecondaryToolbarMode.None });
          }}
          active={true}
          iconName="line"
          title="直線"
          ariaLabel="line" />
      </section>

      <ToolbarItemScroller vertical={isVertical} alignEnd={false} scrollButtons={true}>
        <ToggleToolIconButton
          onClick={(isOn) => {
            const mode = isOn
              ? DrawingMode.StraightLineDrawing
              : DrawingMode.AxisAlignedStraightLineDrawing;
            this.setState({ lastDrawingMode: mode });
            this.props.dispatch(DrawingToolAction.setMode(mode));
          }}
          isOn={this.state.lastDrawingMode ===  DrawingMode.StraightLineDrawing}
          disabled={this.props.drawingTool.mode === DrawingMode.ObjectRemoval}
          iconNameOff="axis-aligned-straight-line"
          iconNameOn="straight-line"
          title="水平垂直 ２点間"
          ariaLabel="toggle-axis-aligned" />
        <ToggleToolIconButton
          onClick={(isOn) => {
            this.props.dispatch(DrawingToolAction.setIsTransparent(isOn));
          }}
          isOn={this.currentDrawingAttr().isTransparent}
          disabled={this.props.drawingTool.mode === DrawingMode.ObjectRemoval}
          iconNameOff="solid-line"
          iconNameOn="transparent-line"
          title="実線 半透過"
          ariaLabel="toggle-line-transparent" />
        {this.createToggleSecondaryButtons()}
        {this.createObjectRemovalButton()}
      </ToolbarItemScroller>
    </>;
  }

  private createCircleShapeLevel = (
    isVertical: boolean,
    flexDirectionStyle: React.CSSProperties,
  ): JSX.Element => {
    return <>
      <section style={{ ...sectionStartStyle, ...flexDirectionStyle }}>
        <ToolIconButton
          onClick={() => {
            this.setState({ level: Level.Base, secondaryToolbar: SecondaryToolbarMode.None });
          }}
          active={true}
          iconName="circle"
          title="円"
          ariaLabel="circle" />
      </section>

      <ToolbarItemScroller vertical={isVertical} alignEnd={false} scrollButtons={true}>
        <ToggleToolIconButton
          onClick={(isOn) => {
            const mode = isOn
              ? DrawingMode.OvalDrawing
              : DrawingMode.CircleDrawing;
            this.setState({ lastDrawingMode: mode });
            this.props.dispatch(DrawingToolAction.setMode(mode));
          }}
          isOn={this.state.lastDrawingMode ===  DrawingMode.OvalDrawing}
          disabled={this.props.drawingTool.mode === DrawingMode.ObjectRemoval}
          iconNameOff="shapekind-circle"
          iconNameOn="shapekind-oval"
          title="円 楕円"
          ariaLabel="toggle-shapekind-circle-or-oval" />
        {this.createObjectDrawingToolButtons()}
      </ToolbarItemScroller>
    </>;
  }

  private createRectangleShapeLevel = (
    isVertical: boolean,
    flexDirectionStyle: React.CSSProperties,
  ): JSX.Element => {
    return <>
      <section style={{ ...sectionStartStyle, ...flexDirectionStyle }}>
        <ToolIconButton
          onClick={() => {
            this.setState({ level: Level.Base, secondaryToolbar: SecondaryToolbarMode.None });
          }}
          active={true}
          iconName="rect"
          title="四角形"
          ariaLabel="rect" />
      </section>

      <ToolbarItemScroller vertical={isVertical} alignEnd={false} scrollButtons={true}>
        <ToggleToolIconButton
          onClick={(isOn) => {
            const mode = isOn
              ? DrawingMode.RectDrawing
              : DrawingMode.SquareDrawing;
            this.setState({ lastDrawingMode: mode });
            this.props.dispatch(DrawingToolAction.setMode(mode));
          }}
          isOn={this.state.lastDrawingMode ===  DrawingMode.RectDrawing}
          disabled={this.props.drawingTool.mode === DrawingMode.ObjectRemoval}
          iconNameOff="shapekind-square"
          iconNameOn="shapekind-rect"
          title="四角 長方形"
          ariaLabel="toggle-shapekind-square-or-rect" />
        {this.createObjectDrawingToolButtons()}
      </ToolbarItemScroller>
    </>;
  }

  private createTriangleShapeLevel = (
    isVertical: boolean,
    flexDirectionStyle: React.CSSProperties,
  ): JSX.Element => {
    return <>
      <section style={{ ...sectionStartStyle, ...flexDirectionStyle }}>
        <ToolIconButton
          onClick={() => {
            this.setState({ level: Level.Base, secondaryToolbar: SecondaryToolbarMode.None });
          }}
          active={true}
          iconName="triangle"
          title="三角形"
          ariaLabel="triangle" />
      </section>

      <ToolbarItemScroller vertical={isVertical} alignEnd={false} scrollButtons={true}>
        <ToggleToolIconButton
          onClick={(isOn) => {
            const mode = isOn
              ? DrawingMode.TriangleDrawing
              : DrawingMode.EquilateralTriangleDrawing;
            this.setState({ lastDrawingMode: mode });
            this.props.dispatch(DrawingToolAction.setMode(mode));
          }}
          isOn={this.state.lastDrawingMode === DrawingMode.TriangleDrawing}
          disabled={this.props.drawingTool.mode === DrawingMode.ObjectRemoval}
          iconNameOff="shapekind-equilateral-triangle"
          iconNameOn="shapekind-triangle"
          title="正三角形 三角形"
          ariaLabel="toggle-shapekind-equilateral-triangle" />
        {this.createObjectDrawingToolButtons()}
      </ToolbarItemScroller>
    </>;
  }

  private createPolygonShapeLevel = (
    isVertical: boolean,
    flexDirectionStyle: React.CSSProperties,
  ): JSX.Element => {
    return <>
      <section style={{ ...sectionStartStyle, ...flexDirectionStyle }}>
        <ToolIconButton
          onClick={() => {
            this.setState({ level: Level.Base, secondaryToolbar: SecondaryToolbarMode.None });
          }}
          active={true}
          iconName="polygon"
          title="多角形"
          ariaLabel="polygon" />
      </section>

      <ToolbarItemScroller vertical={isVertical} alignEnd={false} scrollButtons={true}>
        {this.createObjectDrawingToolButtons()}
      </ToolbarItemScroller>
    </>;
  }

  private createSecondaryToolbarContent(): JSX.Element | JSX.Element[] | null {
    switch (this.state.secondaryToolbar) {
      case SecondaryToolbarMode.UIMode:
        return this.createUIModeSubmenus();
      case SecondaryToolbarMode.Zoom:
        return this.createZoomSubmenus();
      case SecondaryToolbarMode.SNE:
        return this.createSNESubmenus();
      case SecondaryToolbarMode.Undo:
        return this.createUndoSubmenus();
      case SecondaryToolbarMode.ImagePasting:
        return this.createImagePastingSubmenus();
      case SecondaryToolbarMode.ClearEverything:
        return this.createClearEverythingSubmenus();
      case SecondaryToolbarMode.ExtraTools:
        return this.createExtraToolsSubmenus();
      case SecondaryToolbarMode.Color:
        return this.createColorSubmenus();
      case SecondaryToolbarMode.LineWidth:
        return this.createLineWidthSubmenus();
      case SecondaryToolbarMode.LineType:
        return this.createLineTypeSubmenus();
      case SecondaryToolbarMode.None:
        return null;
    }
  }

  private createSecondaryToolbar(): JSX.Element | null {
    const content = this.createSecondaryToolbarContent();
    if (content === null) {
      return null;
    }

    return (
      <SecondaryToolbar direction={this.props.uimode.toolbarDirection}>
        {content}
      </SecondaryToolbar>
    );
  }

  private handleUndo = () => {
    const index = getEditorKeyAndPage(this.props.pages, this.props.publication);
    const page = getPrevDrawingPage(this.props.integratedDrawing, index);
    this.props.dispatch(HistoryAction.undo());

    if (page !== null) {
      PageAction.moveToPage(this.props.pages.activeTab, page, this.props.dispatch);
    }

    // XXX : アクションとメニュー出し入れが同時で使いづらい気がする
    this.setState({ secondaryToolbar: SecondaryToolbarMode.Undo });
  }

  private handleRedoDrawing = () => {
    const index = getEditorKeyAndPage(this.props.pages, this.props.publication);
    const page = getNextDrawingPage(this.props.integratedDrawing, index);
    this.props.dispatch(HistoryAction.redo());

    if (page !== null) {
      PageAction.moveToPage(this.props.pages.activeTab, page, this.props.dispatch);
    }
  }

  private currentDrawingAttr(): DrawingAttr {
    const { mode, drawingAttrs } = this.props.drawingTool;
    const drawingAttr = drawingAttrs[mode] ?
      drawingAttrs[mode] : drawingAttrs[this.state.lastDrawingMode];
    return drawingAttr;
  }

  private handleSelectLineWidth = (index: number) => {
    // XXX: Mock
    const lineWidth = selectableWidths[index];
    switch (this.props.drawingTool.mode) {
      case DrawingMode.Handwriting:
      case DrawingMode.Handerasing: {
        // 手書きと消しゴムの太さレベルが違うと不便なので同じ太さレベルにしてみている
        const handerasingWidth = selectableHanderasingWidths[index];
        this.props.dispatch(DrawingToolAction.setFreehandPenLineWidth(lineWidth, handerasingWidth));
        break;
      }
      default:
        this.props.dispatch(DrawingToolAction.setLineWidth(lineWidth));
        break;
    }
  }

  private lineWidthActiveIndex = () => {
    const selectables = this.props.drawingTool.mode === DrawingMode.Handerasing
      ? selectableHanderasingWidths
      : selectableWidths;
    const index = selectables.indexOf(this.currentDrawingAttr().lineWidth);
    return index === - 1 ? 0 : index;
  }

  private lineTypeActiveIndex = () => {
    switch (this.currentDrawingAttr().lineType) {
      case LineType.Solid: return 0;
      case LineType.Dotted: return 1;
      case LineType.Dashed: return 2;
      case LineType.None: return 3;
    }
  }

  private lineTypeIconName = (): string => {
    switch (this.currentDrawingAttr().lineType) {
      case LineType.Solid: return 'line-type-solid-selected';
      case LineType.Dotted: return 'line-type-dotted-selected';
      case LineType.Dashed: return 'line-type-dashed-selected';
      case LineType.None: return 'line-type-dashed-none';
    }
  }

  private handleSelectLineColor = (index: number) => {
    // XXX: Mock
    const color = selectableColors[index];
    this.props.dispatch(DrawingToolAction.setLineColor(color));
    // FIXME: 塗りの色を独立して指定するUIが無いので線と同じ色にしている
    this.props.dispatch(DrawingToolAction.setFillColor(color));
  }

  private toggleSecondaryToolbarOpen = (secondaryMode:SecondaryToolbarMode) => {
    const toggledState = this.state.secondaryToolbar === secondaryMode
      ? SecondaryToolbarMode.None
      : secondaryMode;
    this.setState({ secondaryToolbar: toggledState });
  }

  private lineColorActiveIndex = () => {
    const drawingAttr = this.currentDrawingAttr();
    const index = selectableColors.indexOf(drawingAttr.lineColor);
    return index === - 1 ? 0 : index;
  }

  private createFreeHandPenLevel = (
    isVertical: boolean,
    flexDirectionStyle: React.CSSProperties,
  ): JSX.Element => {
    return <>
      <section style={{ ...sectionStartStyle, ...flexDirectionStyle }}>
        <ToolIconButton
          onClick={() => {
            this.setState({ level: Level.Base, secondaryToolbar: SecondaryToolbarMode.None });
          }}
          active={true}
          iconName="pen-1"
          title="ペン"
          ariaLabel="pen-1" />
      </section>

      <ToolbarItemScroller vertical={isVertical} alignEnd={false} scrollButtons={true}>
        <ToggleToolIconButton
          onClick={(isOn) => {
            this.props.dispatch(DrawingToolAction.setIsTransparent(isOn));
          }}
          isOn={this.currentDrawingAttr().isTransparent}
          disabled={this.props.drawingTool.mode === DrawingMode.Handerasing}
          iconNameOff="solid-line"
          iconNameOn="transparent-line"
          title="実線 半透過"
          ariaLabel="toggle-line-transparent" />
        {this.createToggleSecondaryButtons(DrawingMode.Handerasing)}
        <ToggleToolIconButton
          onClick={(isOn) => {
            const mode = isOn
              ? DrawingMode.Handerasing
              : this.state.lastDrawingMode;
            this.props.dispatch(DrawingToolAction.setMode(mode));
            this.setState({ secondaryToolbar: SecondaryToolbarMode.None });
          }}
          isOn={this.props.drawingTool.mode === DrawingMode.Handerasing}
          active={this.props.drawingTool.mode === DrawingMode.Handerasing}
          iconNameOn="handeraser"
          iconNameOff="handeraser"
          title="消しゴム"
          aria-label="handeraser" />
      </ToolbarItemScroller>
    </>;
  }

  private createTTSLevel = (
    isVertical: boolean,
    flexDirectionStyle: React.CSSProperties,
  ): JSX.Element => {
    return <>
      <section style={{ ...sectionStartStyle, ...flexDirectionStyle }}>
        <ToolIconButton
          onClick={() => { this.setTTSMode(false); }}
          iconName="sne-tts"
          title="読み上げ"
          ariaLabel="TTS" />
      </section>

      <ToolbarItemScroller vertical={isVertical} alignEnd={false} scrollButtons={true}>
        <TTSToolbar
          rate={this.props.speech.rate}
          status={this.props.speech.status}
          onPlay={this.props.onPlay}
          onPause={this.props.onPause}
          onResume={this.props.onResume}
          onReset={this.props.onReset}
          onRateChange={value => this.props.dispatch(TTSAction.rateChange(value))} />
      </ToolbarItemScroller>
    </>;
  }

  private createSelectionLevel = (
    isVertical: boolean,
    flexDirectionStyle: React.CSSProperties,
  ): JSX.Element => {
    return <>
      <section style={{ ...sectionStartStyle, ...flexDirectionStyle }}>
        <ToolIconButton
          onClick={() => this.props.dispatch(setSelectionState({ type: SelectionType.NONE })) }
          text="選択メニュー"
          title="選択メニュー"
          ariaLabel="Selection" />
      </section>

      <ToolbarItemScroller vertical={isVertical} alignEnd={false} scrollButtons={true}>
        <MarkerMenu />
      </ToolbarItemScroller>
    </>;
  }

  private createMarkerLevel = (
    isVertical: boolean,
    flexDirectionStyle: React.CSSProperties,
  ): JSX.Element => {
    return <>
      <section style={{ ...sectionStartStyle, ...flexDirectionStyle }}>
        <ToolIconButton
          onClick={() => this.setState({ level: Level.Base }) }
          iconName="pen-2"
          title="マーカメニュー"
          ariaLabel="Marker" />
      </section>

      <ToolbarItemScroller vertical={isVertical} alignEnd={false} scrollButtons={true}>
        <MarkerMenu />
      </ToolbarItemScroller>
    </>;
  }

  private handleBasePointerClick = () => {
    if (this.state.secondaryToolbar === SecondaryToolbarMode.UIMode) {
      this.setState({ secondaryToolbar: SecondaryToolbarMode.None });
    } else {
      setUIMode(UIMode.Touch, this.props.dispatch);
      this.setState({ secondaryToolbar: SecondaryToolbarMode.UIMode });
    }
  }

  private handleBaseZoomClick = () => {
    if (this.state.secondaryToolbar === SecondaryToolbarMode.Zoom) {
      setRegionZoomMode(false, this.props.dispatch);
      this.setState({ secondaryToolbar: SecondaryToolbarMode.None });
    } else {
      setRegionZoomMode(true, this.props.dispatch);
      this.setState({ secondaryToolbar: SecondaryToolbarMode.Zoom });
    }
  }

  private handleBaseExtraToolsClick = () => {
    if (this.state.secondaryToolbar === SecondaryToolbarMode.ExtraTools) {
      this.setState({ secondaryToolbar: SecondaryToolbarMode.None });
    } else {
      this.setState({ secondaryToolbar: SecondaryToolbarMode.ExtraTools });
    }
  }

  private createBaseLevel = (
    isVertical: boolean,
  ): JSX.Element => {
    const { integratedDrawing, dispatch, pages, publication } = this.props;
    const canUndo = canUndoDrawing(integratedDrawing, getEditorKeyAndPage(pages, publication));
    const inDrawingMode = this.props.uimode.mode === UIMode.Drawing;

    const drawingMode = this.props.drawingTool.mode;
    const inStickyOrBalloonDrawing =
      (drawingMode === DrawingMode.StickyDrawing || drawingMode === DrawingMode.BalloonDrawing);

    return <>
      <ToolIconButton
        onClick={() => {}}
        iconName="empty"
        title="ユーザ定義ボタン"
        ariaLabel="Empty" />
      <ToolIconButton
        onClick={() => {}}
        iconName="empty"
        title="ユーザ定義ボタン"
        ariaLabel="Empty" />
      <ToolIconButton
        onClick={this.handleBaseExtraToolsClick}
        active={this.state.secondaryToolbar === SecondaryToolbarMode.ExtraTools}
        iconName="bag"
        title="教科固有ツール"
        ariaLabel="Extra tools" />
      <ToolbarItemScroller
        vertical={isVertical}
        alignEnd={false}
        scrollButtons={true}
        initialScrollAmount={this.baseToolbarScrollAmount}
        onUpdate={(scrollAmount) => {
          this.baseToolbarScrollAmount = scrollAmount;
        }}
        >
        {/* 基本ボタン */}
        <ToolIconButton
          onClick={this.handleBasePointerClick}
          active={this.state.secondaryToolbar === SecondaryToolbarMode.UIMode}
          iconName="pointer"
          title="ポインター"
          ariaLabel="Pointer" />
        <ToolIconButton
          onClick={this.handleBaseZoomClick}
          active={this.state.secondaryToolbar === SecondaryToolbarMode.Zoom}
          iconName="zoom"
          title="拡大/縮小"
          ariaLabel="Zoom" />
        <ToolIconButton
          onClick={() => {
            setUIMode(UIMode.Drawing, dispatch);
            this.props.dispatch(DrawingToolAction.setMode(DrawingMode.Handwriting));
            this.setState({
              level: Level.FreehandPen,
              lastDrawingMode: DrawingMode.Handwriting,
              secondaryToolbar: SecondaryToolbarMode.None,
            });
          }}
          active={inDrawingMode && this.props.drawingTool.mode === DrawingMode.Handwriting}
          iconName="pen-1"
          title="ペン（自由）"
          ariaLabel="Pen Free" />
        <ToolIconButton
          onClick={() => {
            this.setState({ level: Level.Marker });
            setUIMode(UIMode.Marker, dispatch);
          }}
          iconName="pen-2"
          title="ペン（マーカ）"
          ariaLabel="pen-2" />
        <ToolIconButton
          onClick={() => {
            setRegionZoomMode(false, dispatch);
            this.setState({ secondaryToolbar: SecondaryToolbarMode.SNE });
          }}
          active={this.state.secondaryToolbar === SecondaryToolbarMode.SNE}
          iconName="sne"
          title="特別支援"
          ariaLabel="SNE" />
        <ToolIconButton
          onClick={this.handleUndo}
          active={this.state.secondaryToolbar === SecondaryToolbarMode.Undo}
          disabled={!canUndo}
          iconName="undo"
          title="UNDO"
          ariaLabel="UNDO" />
        <ToolIconButton
          onClick={() => {
            setUIMode(UIMode.Drawing, dispatch);
            this.props.dispatch(
              DrawingToolAction.setMode(DrawingMode.AxisAlignedStraightLineDrawing),
            );
            this.setState({
              level: Level.LineType,
              lastDrawingMode: DrawingMode.AxisAlignedStraightLineDrawing,
              secondaryToolbar: SecondaryToolbarMode.None,
            });
          }}
          active={
            inDrawingMode &&
            (this.props.drawingTool.mode === DrawingMode.AxisAlignedStraightLineDrawing
             || this.props.drawingTool.mode === DrawingMode.StraightLineDrawing)
          }
          iconName="line"
          title="直線"
          ariaLabel="Line" />
        <ToolIconButton
          onClick={() => {
            setUIMode(UIMode.Drawing, dispatch);
            this.props.dispatch(DrawingToolAction.setMode(DrawingMode.CircleDrawing));
            this.setState({
              level: Level.CircleType,
              lastDrawingMode: DrawingMode.CircleDrawing,
              secondaryToolbar: SecondaryToolbarMode.None,
            });
          }}
          active={
            inDrawingMode &&
            (this.props.drawingTool.mode === DrawingMode.CircleDrawing
             || this.props.drawingTool.mode === DrawingMode.OvalDrawing)
          }
          iconName="circle"
          title="円"
          ariaLabel="circle" />
        <ToolIconButton
          onClick={() => {
            setUIMode(UIMode.Drawing, dispatch);
            this.props.dispatch(DrawingToolAction.setMode(DrawingMode.SquareDrawing));
            this.setState({
              level: Level.RectangleType,
              lastDrawingMode: DrawingMode.SquareDrawing,
              secondaryToolbar: SecondaryToolbarMode.None,
            });
          }}
          active={
            inDrawingMode &&
            (this.props.drawingTool.mode === DrawingMode.SquareDrawing
             || this.props.drawingTool.mode === DrawingMode.RectDrawing)
          }
          iconName="rect"
          title="四角形"
          ariaLabel="rect" />
        <ToolIconButton
          onClick={() => {
            setUIMode(UIMode.Drawing, dispatch);
            this.props.dispatch(DrawingToolAction.setMode(DrawingMode.EquilateralTriangleDrawing));
            this.setState({
              level: Level.TriangleType,
              lastDrawingMode: DrawingMode.EquilateralTriangleDrawing,
              secondaryToolbar: SecondaryToolbarMode.None,
            });
          }}
          active={
            inDrawingMode &&
            (this.props.drawingTool.mode === DrawingMode.EquilateralTriangleDrawing
             || this.props.drawingTool.mode === DrawingMode.TriangleDrawing)
          }
          iconName="triangle"
          title="三角形"
          ariaLabel="triangle" />
        <ToolIconButton
          onClick={() => {
            setUIMode(UIMode.Drawing, dispatch);
            this.props.dispatch(DrawingToolAction.setMode(DrawingMode.MultiVertexDrawing));
            this.setState({
              level: Level.PolygonType,
              lastDrawingMode: DrawingMode.MultiVertexDrawing,
              secondaryToolbar: SecondaryToolbarMode.None,
            });
          }}
          active={inDrawingMode && this.props.drawingTool.mode === DrawingMode.MultiVertexDrawing}
          iconName="polygon"
          title="多角形"
          ariaLabel="polygon" />
        <ToolIconButton
          onClick={ () => alert('stamp') }
          iconName="stamp"
          title="スタンプ"
          ariaLabel="stamp" />
        <ToolIconButton
          onClick={this.props.onSendToClipbook}
          iconName="note-binder"
          title="ノート・バインダ"
          ariaLabel="note-binder" />
        <ToolIconButton
          onClick={ () => alert('screenshot') }
          iconName="screenshot"
          title="スクリーンショット"
          ariaLabel="screenshot" />
        <ToolIconButton
          onClick={() => { this.setState({ showMaskAndBalloonSettingsDialog: true }); }}
          active={inDrawingMode && inStickyOrBalloonDrawing}
          iconName="mask-balloon"
          title="マスク/吹き出し"
          ariaLabel="mask/balloon" />
        <ToolIconButton
          onClick={() => {
            this.setState({ secondaryToolbar: SecondaryToolbarMode.ImagePasting });
          }}
          active={inDrawingMode && this.props.drawingTool.mode === DrawingMode.ImagePasting}
          iconName="image-pasting"
          title="画像貼り付け"
          ariaLabel="image-pasting" />
        <ToolIconButton
          onClick={() => { this.setState({ showMultiroleLinkDialog: true }); }}
          active={inDrawingMode && this.props.drawingTool.mode === DrawingMode.MultiroleLinkPasting}
          disabled={
            getBookmarkReferenceIfPageableTab(
              this.props.pages, this.props.publication,
            ) === undefined
          }
          iconName="mac-dock-url-icon"
          title="マイリンク"
          ariaLabel="multiroleLink" />
        <ToolIconButton
          onClick={ () => alert('inter-app-cooperation') }
          iconName="inter-app-cooperation"
          title="アプリ間連携"
          ariaLabel="inter-app-cooperation" />
      </ToolbarItemScroller>
    </>;
  }

  public render() {
    const { toolbarDirection } = this.props.uimode;
    const isVertical = (toolbarDirection !== ToolbarDirection.Horizontal);
    const flexDirectionStyle: React.CSSProperties =
      isVertical ? { flexDirection: 'column', width: `${toolbarSize}px` }
        : { flexDirection: 'row', height: `${toolbarSize}px` };

    const content = ((): JSX.Element => {
      if (this.props.selection.type !== SelectionType.NONE) {
        return this.createSelectionLevel(isVertical, flexDirectionStyle);
      }

      switch (this.state.level) {
        case Level.Base:
          return this.createBaseLevel(isVertical);
        case Level.FreehandPen:
          return this.createFreeHandPenLevel(isVertical, flexDirectionStyle);
        case Level.LineType:
          return this.createStraightLineLevel(isVertical, flexDirectionStyle);
        case Level.CircleType:
          return this.createCircleShapeLevel(isVertical, flexDirectionStyle);
        case Level.RectangleType:
          return this.createRectangleShapeLevel(isVertical, flexDirectionStyle);
        case Level.TriangleType:
          return this.createTriangleShapeLevel(isVertical, flexDirectionStyle);
        case Level.PolygonType:
          return this.createPolygonShapeLevel(isVertical, flexDirectionStyle);
        case Level.TTS:
          return this.createTTSLevel(isVertical, flexDirectionStyle);
        case Level.Marker:
          return this.createMarkerLevel(isVertical, flexDirectionStyle);
      }
    })();

    return (
      <PrimaryToolbar direction={toolbarDirection}>
        {content}
        {this.createSecondaryToolbar()}
        <MaskAndBalloonSettingsDialog
          isOpen={this.state.showMaskAndBalloonSettingsDialog}
          onClose={(drawingAttr, selectedDrawingMode) => {
            if (drawingAttr !== null) {
              if (selectedDrawingMode !== null) {
                setUIMode(UIMode.Drawing, this.props.dispatch);
                this.props.dispatch(DrawingToolAction.setMode(selectedDrawingMode));
              }
              this.props.dispatch(DrawingToolAction.updateNoteDrawingtAttr(drawingAttr));
            }
            this.setState({ showMaskAndBalloonSettingsDialog: false });
          }}/>
          {/* TODO multiroleLinkDialog} */}
      </PrimaryToolbar>
    );
  }
}

const mapStateToProps = (appStates: AppStates) => {
  const { pages } = appStates;
  const tab = pages.tabs[pages.activeTab];
  const panelID = tab.focus === tabFocusSelf ? tab.id : tab.focus;
  return {
    ...mapStateToZoomable(appStates, { panelID }),
    pages: appStates.pages,
    preference: appStates.preference,
    speech: appStates.speech,
    uimode: appStates.uimode,
    drawingTool: appStates.drawingTool,
    integratedDrawing: appStates.integratedDrawing,
    selection: appStates.selectionState,
    bookmarks: appStates.bookmarks,
  };
};

export default connect(mapStateToProps)(HeaderToolbar);
